import { PropsWithChildren, ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdCircle } from 'react-icons/md';
import { TransmedicsLogoIcon } from '@icons';
import { cn, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, useDisclosure } from '@ds';
import { getLocalTimeZone } from '@internationalized/date';
import { PermissionType } from '@/permissions';
import { SignOutModal } from './components/SignOutModal';
import { UserMenu, UserMenuProps } from './components/UserMenu';
import { SideMenuTheme } from './types';
import { getShortTzByIanaName } from '@/utils/helpers';
import { ConditionalRender } from '../ConditionalRender';

const getLinkStyles = (isActive: boolean, menuColor?: string): string => {
  const defaultStyles = 'flex flex-col place-items-center gap-1 border-l-4 cursor-pointer';
  if (isActive) {
    return `${defaultStyles} text-base-90 border-base-90`;
  }

  return `${defaultStyles} ${menuColor || 'text-base-30'} border-transparent hover:text-base-90 hover:border-base-90`;
};

type SingleMenuItem = {
  to: string;
  permissions?: PermissionType[];
  label: ReactNode;
};

type NestedMenuItem = {
  key: string;
  label: ReactNode;
  permissions?: PermissionType[];
  children: SingleMenuItem[];
};

export type MenuItem = SingleMenuItem | NestedMenuItem;

export type SideMenuProps = {
  logout: ({ forceLogout }?: { forceLogout?: boolean }) => Promise<void>;
  getUserMenu: UserMenuProps['getUserMenu'];
  mainMenu: MenuItem[];
  timezone?: string;
  userName?: string;
  photoPath?: string;
  theme?: SideMenuTheme;
  // TODO remove containerClassName
  containerClassName?: string;
};

export const SideMenu = ({
  logout,
  getUserMenu,
  mainMenu,
  timezone,
  userName,
  photoPath,
  theme,
  containerClassName = '',
  children,
}: SideMenuProps & PropsWithChildren) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userTimezone = getShortTzByIanaName(getLocalTimeZone());
  const displayedTimezone = timezone || userTimezone;

  const handleSignOut = async () => {
    await logout();
    onClose();
    toast.success('Log out successfully.');
  };

  const getIsActive = (path: string) => pathname.includes(path);

  const isAdmin = pathname.includes('admin');
  const logoSize = 64;
  const blockSize = 'w-[80px]';

  const renderNestedMenuItem = (menuItem: NestedMenuItem) => {
    return (
      <Dropdown
        key={menuItem.key}
        placement="left-start"
        triggerScaleOnOpen={false}
        showArrow
        classNames={{
          base: 'before:bg-base-5',
          content: 'p-1 bg-base-5 rounded-md min-w-[140px]',
        }}
      >
        <DropdownTrigger>
          <div className={getLinkStyles(getIsActive(menuItem.key), theme?.primaryMenuColor)}>{menuItem.label}</div>
        </DropdownTrigger>
        <DropdownMenu itemClasses={{ base: 'rounded-md text-base-90 transition-all' }}>
          {menuItem.children.map((child) => (
            <DropdownItem
              key={child.to}
              textValue={child.to}
              className="h-6 gap-2 px-1 py-0.5"
              onClick={() => navigate(child.to)}
              startContent={<MdCircle size={5} opacity={getIsActive(child.to) ? 1 : 0} />}
            >
              {child.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  return (
    <>
      <div
        className={cn(
          blockSize,
          'py-6 flex flex-col shrink-0 items-center justify-between relative z-40',
          !containerClassName && !theme ? 'bg-base-5' : theme?.bg,
          containerClassName,
        )}
      >
        <Link to="/" className="text-base-70 hover:text-base-70">
          <TransmedicsLogoIcon width={logoSize} height={logoSize} />
        </Link>
        {!isAdmin && (
          <div className="flex flex-col gap-10 w-full">
            {mainMenu.map((item) => {
              if ('children' in item) return renderNestedMenuItem(item);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => getLinkStyles(isActive, theme?.primaryMenuColor)}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        )}
        <div className="flex flex-col gap-5 w-full items-center">
          {children}
          <UserMenu onOpen={onOpen} getUserMenu={getUserMenu} userName={userName} photoPath={photoPath} />
          <ConditionalRender show={Boolean(displayedTimezone)}>
            <div
              className={cn(
                'w-auto py-1 px-2 rounded min-h-[32px] text-base-90',
                {
                  'font-bold': isAdmin,
                  'bg-base-10': !theme?.secondaryMenuBgColor,
                },
                theme?.secondaryMenuBgColor,
              )}
            >
              {displayedTimezone}
            </div>
          </ConditionalRender>
        </div>
      </div>
      <SignOutModal isOpen={isOpen} onClose={onClose} onSignOut={handleSignOut} />
    </>
  );
};
